import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  UserAvatar
} from "@/components"
import { DiscussionWithTop } from "@/models/Discussions"
import { useState } from "react"
import { formatName } from "@/lib/utils"
import { commentOnLinesFormat } from "@/components/designSystem/Discussions/utils"
import { useTranslation } from "react-i18next"

interface Props {
  isExpanded: boolean
  remainingDiscussions: DiscussionWithTop[]
  handleClick: (id: string, isRemaining: boolean) => void
  remainingCount: number
  isRemainingButtonClicked: boolean
  selectedDiscussionId?: string
}

export function RemainingDiscussionsChips({
  isExpanded,
  remainingDiscussions,
  handleClick,
  remainingCount,
  isRemainingButtonClicked,
  selectedDiscussionId
}: Props) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { t } = useTranslation("Discussions")

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen} modal={false}>
      <DropdownMenuContent
        align="start"
        onInteractOutside={() => setIsDropdownOpen(false)}
        className="translate-x-[-30px] pr-0"
      >
        <section className="max-h-72 overflow-y-scroll">
          {remainingDiscussions.map((remainingDiscussion) => {
            const { id, outdated, author_user: authorUser, author: authorURL } = remainingDiscussion
            const isSelected = id === selectedDiscussionId

            return (
              <DropdownMenuItem
                key={id}
                className={`flex items-center px-1 text-xs ${isSelected ? "bg-fill" : ""}`}
                tabIndex={0}
                onClick={() => {
                  handleClick(id, true)
                  setIsDropdownOpen(false)
                }}
              >
                <div className="mr-2 size-6 justify-center rounded-full border border-border bg-background p-0">
                  <UserAvatar
                    className="size-full"
                    username={authorUser?.display_name || formatName(authorUser?.gh_login)}
                    avatarURL={authorUser?.avatar_url}
                  />
                </div>
                {authorUser?.display_name || authorURL}
                <span className="ml-1 flex flex-wrap text-xs leading-5 text-secondary">
                  {commentOnLinesFormat(remainingDiscussion)}
                </span>
                {outdated && <span className="ml-1 lowercase text-error-dark"> {t("Outdated")}</span>}
              </DropdownMenuItem>
            )
          })}
        </section>
      </DropdownMenuContent>
      <DropdownMenuTrigger className="outline-none" asChild>
        <div className="z-10">
          <div
            className={`flex size-8 items-center justify-center rounded-full ${
              isRemainingButtonClicked && isExpanded ? "bg-tint" : "bg-background"
            }`}
          >
            <div className="z-0 flex size-7 items-center justify-center rounded-full bg-hint p-0.5">
              <Button
                variant="ghost"
                className="flex size-6 items-center justify-center rounded-full bg-background p-0.5"
              >
                <span className="size-full leading-5">+{remainingCount}</span>
              </Button>
            </div>
          </div>
        </div>
      </DropdownMenuTrigger>
    </DropdownMenu>
  )
}
